import React from 'react';
import TrackingCell from '../../components/TrackingCell/TrackingCell';
import { RescueOrderShipmentGql, RescueOrderShipmentStatus } from '../../lib/rescueQueries';
import { formatDate } from '../../lib/utils';
import shipmentStyles from '../../styles/shipmentTable.module.scss';
import styles from './Devices.module.scss';

const columns = ['Order #', 'Device Count', 'Status', 'Order Date', 'Tracking'];

type VisibleShipmentStatus = Exclude<RescueOrderShipmentStatus, 'CANCELED'>;

const STATUS_LABELS: Record<VisibleShipmentStatus, string> = {
  ORDERED: 'Order Received',
  SHIPPED: 'In Transit',
};

const STATUS_WITH_DOT: ReadonlySet<VisibleShipmentStatus> = new Set(['ORDERED', 'SHIPPED']);

const STATUS_BADGE_CLASS: Record<VisibleShipmentStatus, string> = {
  ORDERED: styles.statusBadgeOrdered,
  SHIPPED: styles.statusBadgeShipped,
};

interface DevicesInTransitTableProps {
  shipments: RescueOrderShipmentGql[];
}

function isVisible(
  shipment: RescueOrderShipmentGql,
): shipment is RescueOrderShipmentGql & { shipmentStatus: VisibleShipmentStatus } {
  return shipment.shipmentStatus !== 'CANCELED';
}

export default function DevicesInTransitTable({ shipments }: DevicesInTransitTableProps) {
  const visibleShipments = shipments.filter(isVisible);
  if (visibleShipments.length === 0) {
    return null;
  }

  const totalCount = visibleShipments.length;

  return (
    <div className={`${styles.sectionCard} ${styles.sectionCardInfo}`}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>Devices in Transit ({totalCount})</h3>
        <p className={styles.sectionDescription}>
          Outstanding shipments &mdash; orders placed but not yet delivered to your rescue.
        </p>
      </div>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <colgroup>
            <col style={{ width: '24%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '26%' }} />
          </colgroup>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleShipments.map((shipment, idx) => {
              const [primaryOrder, ...additional] = shipment.orderNumbers;
              return (
                <tr key={`${primaryOrder ?? 'shipment'}-${idx}`}>
                  <td>
                    <div>{primaryOrder ?? '\u2014'}</div>
                    {additional.length > 0 && (
                      <div className={shipmentStyles.orderNumbersList}>+{additional.join(', ')}</div>
                    )}
                  </td>
                  <td>{shipment.deviceCount}</td>
                  <td>
                    <span className={STATUS_BADGE_CLASS[shipment.shipmentStatus]}>
                      {STATUS_WITH_DOT.has(shipment.shipmentStatus) && (
                        <span className={shipmentStyles.statusBadgeDot} />
                      )}
                      {STATUS_LABELS[shipment.shipmentStatus]}
                    </span>
                  </td>
                  <td>{formatDate(shipment.orderDate)}</td>
                  <td>
                    <TrackingCell trackingNumber={shipment.trackingNumber} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
